/**
 * 这一期开出的是几号。
 *
 * <p><b>为什么要推导，而不是只读 `winners`。</b> 结果弹窗原本只在
 * `winners.length > 0` 时才显示开奖号码。可是「没有中奖记录」和「没有开奖」
 * 是两回事：一期 160 人参与、正常开出号码、但那个序号没有对应到任何中奖
 * 记录时，用户看到的是「你的 6 个号码没中」却**通篇没有开奖号码** ——
 * 一句无法核对的话。而这个号码本来就是公开公式算出来的，不是后端的秘密。
 *
 * <p>公式与验证步骤（`fairExampleStep1`）逐字一致：
 * {@code 序号 = 开奖数字 mod 参与数}，序号从 0 开始（第一张票是 000）。
 */

/**
 * @param winnerSerial 后端给的中奖序号。线上与展示**同为 0 起**，接口原样透出
 *   库里的号，这里不再做任何加减（2026-09-24 PM 拍板；在那之前是库里 1 起、
 *   接口 −1、前端 +1 三层互相抵消，漏补一处就差 1）。
 *   **存在就以它为准** —— 万一哪天公式或取模口径变了，屏幕上也绝不会出现一个
 *   和后端结算不一致的号码。推导只在后端没给的时候兜底。
 * @returns 票号（0 起，玩家该看到的那个数）；无法确定时返回 `null`，调用方
 *   应当**不显示这一行**。返回 0 不行 —— 0 起之后 000 是第一张票的真号码，
 *   拿它兜底就等于凭空宣布「第一张票中了」，且无法与真中奖分辨
 */
export function drawnSerialFrom(
  winnerSerial: number | string | null | undefined,
  digits: string | null | undefined,
  totalEntries: number | null | undefined,
  serialBase: number | null | undefined = 0,
): number | null {
  // 线上值就是玩家看到的号，不做换算。以前这里 +1，而 History、钱包页又各自
  // +1 —— 三处各补一次，「线上给了 0 号」这条路上谁都补不到，结果页把第 1 号
  // 印成了 002（2026-09-18 复核）。票号改成 0 起后换算层整个消失，契约只剩
  // 一条：进去是线上值，出来是同一个值。
  const fromServer = toWireSerial(winnerSerial);
  if (fromServer !== null) return fromServer;

  const clean = String(digits ?? '').replace(/\D/g, '');
  const entries = Number(totalEntries);
  // 参与数为 0 时不存在「第几号」。少了这个判断，BigInt % 0n 会抛，
  // 退化路径上 x % 0 又是 NaN —— 两条路都会变成一个假号码。
  if (!clean || !Number.isFinite(entries) || entries <= 0) return null;

  return moduloDecimalString(clean, entries) + (serialBase === 1 ? 1 : 0);
}

/** 十进制字符串取模。开奖数字远超 `Number.MAX_SAFE_INTEGER`，必须走 BigInt。 */
export function moduloDecimalString(value: string, divisor: number): number {
  try {
    return Number(BigInt(value) % BigInt(divisor));
  } catch {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric % divisor : 0;
  }
}

/**
 * 线上那个 0 起的序号。
 *
 * <p><b>0 是合法值</b>（就是第一张票 000），不能把它当成「没有」—— 那样第一张
 * 票中奖的那一期就会掉到下面的推导分支去，而推导分支在缺 `d_digits` 时返回
 * `null`，整行开奖号码就消失了。负数才是没有。
 */
function toWireSerial(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const serial = Number(value);
  return Number.isFinite(serial) && serial >= 0 ? Math.trunc(serial) : null;
}
